import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { NAV, SHOP_URL } from "./nav";

const linkCls = ({ isActive }) =>
  `relative px-1 py-2 text-[0.9rem] font-medium transition-colors duration-300 ${
    isActive ? "text-[#F3C649]" : "text-[#E6E3DA] hover:text-[#F3C649]"
  }`;

const MobileMenu = ({ onClose }) => (
  <motion.div
    id="mobile-menu"
    data-lenis-prevent
    className="fixed inset-0 z-40 overflow-y-auto bg-[#0A0B0E] px-6 pb-12 pt-28 xl:hidden"
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
  >
    <nav aria-label="Mobile">
      <ul className="space-y-1">
        {NAV.map((n, i) => (
          <motion.li key={n.to} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.05 * i }}>
            <NavLink to={n.to} end onClick={onClose} data-testid={`mobile-nav-${n.id}`}
              className={({ isActive }) => `block border-b border-white/10 py-4 font-display text-3xl ${isActive ? "text-[#F3C649]" : "text-[#F9F8F6]"}`}>
              {n.label}
            </NavLink>
          </motion.li>
        ))}
        <motion.li initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.05 * NAV.length }}>
          <a href={SHOP_URL} target="_blank" rel="noopener noreferrer" onClick={onClose} data-testid="mobile-nav-shop"
            className="flex items-center gap-2 border-b border-white/10 py-4 font-display text-3xl text-[#F3C649]">
            Shop <ArrowUpRight size={22} aria-hidden="true" /><span className="sr-only"> (opens The Journey of Life store in a new tab)</span>
          </a>
        </motion.li>
      </ul>
      <Link to="/contact" onClick={onClose} className="btn-gold mt-10" data-testid="mobile-nav-cta">
        Discuss a Partnership <ArrowUpRight size={18} aria-hidden="true" />
      </Link>
    </nav>
  </motion.div>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  useEffect(() => {
    if (open) window.__lenis?.stop(); else window.__lenis?.start();
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${open ? "bg-[#0A0B0E]" : scrolled ? "border-b border-[#D4AF37]/20 bg-[#0A0B0E]/85 backdrop-blur-xl" : "bg-transparent"}`}>
      <div className="mx-auto flex h-20 max-w-[1400px] items-center justify-between px-5 sm:px-8">
        <Link to="/" aria-label="JEM home" data-testid="header-logo-link"><Logo /></Link>
        <nav aria-label="Main" className="hidden xl:block">
          <ul className="flex items-center gap-6">
            {NAV.slice(1).map((n) => (
              <li key={n.to}><NavLink to={n.to} className={linkCls} data-testid={`nav-${n.id}`}>{n.label}</NavLink></li>
            ))}
            <li><a href={SHOP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-1 py-2 text-[0.9rem] font-medium text-[#F3C649] transition-colors hover:text-[#FFE7A3]" data-testid="nav-shop">Shop<ArrowUpRight size={14} aria-hidden="true" /><span className="sr-only"> (opens The Journey of Life store in a new tab)</span></a></li>
          </ul>
        </nav>
        <Link to="/contact" className="btn-gold hidden !py-3 xl:inline-flex" data-testid="header-cta">Discuss a Partnership</Link>
        <button type="button" className="relative z-50 rounded-full border border-[#D4AF37]/50 p-3 text-[#F9F8F6] xl:hidden"
          aria-expanded={open} aria-controls="mobile-menu" aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle">
          {open ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
        </button>
      </div>
      <AnimatePresence>{open && <MobileMenu onClose={() => setOpen(false)} />}</AnimatePresence>
    </header>
  );
};
